import type { Section } from '@/types/chat';
import { SectionContainer } from './SectionContainer';
import { DecorationNote } from './DecorationSections';
import { ProductRecommendationsView } from './ProductRecommendations';
import { ProductJustificationView } from './ProductJustification';
import { ProfessionalsListView } from './ProfessionalsList';
import { EmergencyCtaView } from './EmergencyCta';
import { RelatedPagesView } from './RelatedPages';
import { SafetyFollowupNoticeView } from './SafetyFollowupNotice';

export interface SectionRendererProps {
  section: Section;
}

export function SectionRenderer({ section }: SectionRendererProps): JSX.Element | null {
  // Defensive: no validated backend can currently emit a null/non-object
  // entry inside structured_reply.sections, but if one slips through it
  // must not crash the whole widget with an uncaught TypeError.
  if (!section || typeof section !== 'object') return null;

  switch (section.type) {
    case 'analysis':
    case 'concept_explanation':
    case 'lifestyle_advice':
    case 'direct_answer':
      return <SectionContainer title={section.title} body={section.body_md} />;
    case 'pathology_recognition':
    case 'feedback_acknowledgment':
    case 'wellness_invitation':
    case 'good_reflex_tip':
      return <SectionContainer body={section.body_md} />;
    case 'skill_intro':
    case 'product_precaution':
    case 'red_flags':
    case 'ai_notice':
      return <DecorationNote body={section.body_md} />;
    case 'product_recommendations':
      return <ProductRecommendationsView section={section} />;
    case 'product_justification':
      return <ProductJustificationView section={section} />;
    case 'professionals_list':
      return <ProfessionalsListView section={section} />;
    case 'emergency_cta':
      return <EmergencyCtaView section={section} />;
    case 'related_pages':
      return <RelatedPagesView section={section} />;
    case 'safety_followup_notice':
      return <SafetyFollowupNoticeView section={section} />;
    default:
      // Unknown section type — render nothing (forward-compat with new backend types).
      return null;
  }
}
